import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import {
  createReport,
  getReport,
  updateReport,
  updateStep,
  createStep,
  deleteStep,
  uploadImage,
  updateImage,
  uploadAnnotatedImage,
  uploadCroppedImage,
  resetCroppedImage,
  deleteImage,
  reorderImages,
  downloadPdf,
  listCustomers,
  updateChecklistItem,
  addChecklistItem,
  deleteChecklistItem,
  getAiStatus,
  aiConclusion,
  aiReview,
  listStepImages,
  subscribeOfflineQueue,
} from '../services/api';
import ImageAnnotator from '../components/ImageAnnotator';
import ImageCropper from '../components/ImageCropper';
import PhotoDropzone from '../components/PhotoDropzone';
import MobileUploadModal from '../components/MobileUploadModal';
import useFileDrop from '../hooks/useFileDrop';
import useDragAutoScroll from '../hooks/useDragAutoScroll';
import AuthImage from '../components/AuthImage';
import AiEnrich, { AiPreview, aiErrorKey } from '../components/AiEnrich';
import AutoGrowTextarea from '../components/AutoGrowTextarea';
import ConfirmDialog from '../components/ConfirmDialog';
import DiffText from '../components/DiffText';
import ShareLinkPanel from '../components/ShareLinkPanel';
import SendReportDialog from '../components/SendReportDialog';
import NewClientDialog from '../components/NewClientDialog';
import { compressImage } from '../utils/imageCompress';
import { photoNumbers } from '../utils/photoNumbers';
import { addressAfterClientChange } from '../utils/clientAddress';
import { openPdfInNewTab } from '../utils/openPdf';
import { apiErrorMessage } from '../utils/apiError';

const WIZARD_PAGES = ['info', 'steps', 'checklist', 'conclusion'];

// Storage can be unavailable (private mode, blocked site data): never fatal.
const lastStepKey = (reportId) => `expertisr.lastStep.${reportId}`;
function readLastStep(reportId) {
  try { return window.localStorage.getItem(lastStepKey(reportId)); } catch { return null; }
}
function writeLastStep(reportId, stepId) {
  try { window.localStorage.setItem(lastStepKey(reportId), stepId); } catch { /* ignore */ }
}

export default function ReportWizard() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(!!id);
  const [saving, setSaving] = useState(false);
  const [savedVisible, setSavedVisible] = useState(false);
  const [wizardPage, setWizardPage] = useState(0);
  const [activeStepIdx, setActiveStepIdx] = useState(0);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [annotatingImage, setAnnotatingImage] = useState(null);
  const [croppingImage, setCroppingImage] = useState(null);
  const [qrStepId, setQrStepId] = useState(null);
  // What is about to be deleted, waiting for confirmation:
  // { kind: 'step', step } or { kind: 'image', stepId, image, n }.
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [orderError, setOrderError] = useState('');
  const [dragImageId, setDragImageId] = useState(null);
  // A photo being dragged scrolls the page near the window's edges.
  useDragAutoScroll(Boolean(dragImageId));
  // Checkpoints added on this report only.
  const [newPoint, setNewPoint] = useState('');
  const [pointError, setPointError] = useState('');
  const [renaming, setRenaming] = useState(null); // { id, label }

  // Company / Customer
  // One list of clients: either selector (facturation, intervention) picks from it.
  const [customers, setCustomers] = useState([]);
  // « Nouveau client » dialog: null, "customer" (intervention) or "company" (facturation).
  const [newClientFor, setNewClientFor] = useState(null);

  // Form fields for page 0 (info)
  const [form, setForm] = useState({
    title: '',
    reference: '',
    description: '',
    client_name: '',
    client_address: '',
    location: '',
    expertise_date: '',
    requesting_company_id: '',
    customer_id: '',
  });

  const [conclusion, setConclusion] = useState('');
  const [sendOpen, setSendOpen] = useState(false); // top-bar e-mail dialog
  const [sentNotice, setSentNotice] = useState('');

  // One debounce slot per logical target ("report", "step:<id>:content",
  // "checklist:<id>", ...). A single shared timer used to let an edit to one
  // field cancel the pending save of another — losing the first edit outright,
  // which is what made the AI "Replace" on a step look like it did nothing.
  const timersRef = useRef({});
  const debounceSave = useCallback((key, fn, delay = 800) => {
    const slot = timersRef.current[key];
    if (slot) clearTimeout(slot.timer);
    timersRef.current[key] = { fn, timer: setTimeout(() => {
      delete timersRef.current[key];
      fn();
    }, delay) };
  }, []);

  // Run every pending debounced save now and wait for them: the server renders
  // the PDF and the e-mail from what it holds, not from what is on screen.
  const flushPendingSaves = useCallback(async () => {
    const slots = timersRef.current;
    timersRef.current = {};
    await Promise.all(Object.values(slots).map(async ({ timer, fn }) => {
      clearTimeout(timer);
      try { await fn(); } catch { /* the save reports its own failure */ }
    }));
  }, []);

  // Leaving the wizard must not drop the last, still-pending edit.
  useEffect(() => () => {
    const slots = timersRef.current;
    timersRef.current = {};
    Object.values(slots).forEach(({ timer, fn }) => {
      clearTimeout(timer);
      try { fn(); } catch { /* fire-and-forget */ }
    });
  }, []);

  // AI assistance
  const [aiStatus, setAiStatus] = useState({ enabled: false });
  // Photos only when the expert asks for them: vision is where the model used
  // to "see" damage nobody had noted.
  const [aiWithPhotos, setAiWithPhotos] = useState(false);
  const [aiConc, setAiConc] = useState({ loading: false, text: null, error: '' });
  // Whole-report proofreading pass: one correction per field, applied à la carte.
  const [aiReviewState, setAiReviewState] = useState({
    loading: false, items: null, checked: 0, skipped: 0, error: '',
  });

  // Load the clients
  useEffect(() => {
    listCustomers().then(setCustomers).catch(() => {});
    getAiStatus()
      .then(setAiStatus)
      .catch(() => {});
  }, []);

  // Load existing report
  useEffect(() => {
    if (!id) return;
    getReport(id)
      .then((r) => {
        setReport(r);
        setForm({
          title: r.title || '',
          reference: r.reference || '',
          description: r.description || '',
          client_name: r.client_name || '',
          client_address: r.client_address || '',
          location: r.location || '',
          expertise_date: r.expertise_date || '',
          requesting_company_id: r.requesting_company_id || '',
          customer_id: r.customer_id || '',
        });
        setConclusion(r.conclusion || '');
        // Reopen where the expert left off: the page, and on "Steps" the step.
        setWizardPage(Math.min(Math.max(r.current_step || 0, 0), WIZARD_PAGES.length - 1));
        const lastStepId = readLastStep(r.id);
        const stepIdx = (r.steps || []).findIndex((s) => s.id === lastStepId);
        if (stepIdx > 0) setActiveStepIdx(stepIdx);
      })
      .catch(() => navigate('/reports'))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  // Auto-save with debounce
  const flashSaved = useCallback(() => {
    setSavedVisible(true);
    setTimeout(() => setSavedVisible(false), 2000);
  }, []);

  const saveReport = useCallback(
    async (updates = {}) => {
      if (!report) return;
      setSaving(true);
      try {
        // No current_step here: this save may fire after the expert has
        // already moved on (debounced, or flushed on leaving) and used to
        // write the page the edit was typed on over the one reached since —
        // the report then reopened on "Information". Only goToPage saves it.
        const updated = await updateReport(report.id, updates);
        setReport(updated);
        flashSaved();
      } catch {
        // silent
      } finally {
        setSaving(false);
      }
    },
    [report, flashSaved]
  );

  // Report-level fields share one slot: they are merged into a single PATCH,
  // so coalescing successive edits is intended here.
  const pendingReportRef = useRef({});
  const debouncedSave = useCallback(
    (updates) => {
      pendingReportRef.current = { ...pendingReportRef.current, ...updates };
      debounceSave('report', () => {
        const merged = pendingReportRef.current;
        pendingReportRef.current = {};
        return saveReport(merged);
      });
    },
    [saveReport, debounceSave]
  );

  // Create new report
  const handleCreate = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.requesting_company_id) delete payload.requesting_company_id;
      if (!payload.customer_id) delete payload.customer_id;
      const r = await createReport(payload);
      navigate(`/reports/${r.id}`, { replace: true });
    } catch {
      // silent
    } finally {
      setSaving(false);
    }
  };

  // Page 0: info form change
  // `chosen` is the client object when the caller just created it (it is not
  // in `customers` yet within this render).
  const handleFormChange = (field, value, chosen = null) => {
    const extra = {};
    if (field === 'customer_id') {
      // The place of the expertise defaults to the on-site client's address.
      const location = addressAfterClientChange(
        form.location,
        customers.find((c) => c.id === form.customer_id),
        chosen || customers.find((c) => c.id === value),
      );
      if (location !== form.location) extra.location = location;
    }
    setForm((prev) => ({ ...prev, [field]: value, ...extra }));
    if (report) {
      const update = { [field]: value, ...extra };
      if ((field === 'requesting_company_id' || field === 'customer_id') && !value) {
        update[field] = null;
      }
      debouncedSave(update);
    }
  };

  // Page 3: conclusion change
  const handleConclusionChange = (value) => {
    setConclusion(value);
    debouncedSave({ conclusion: value });
  };

  // AI: generate a conclusion from the report elements (level = cost/depth)
  const handleGenerateConclusion = async () => {
    if (!report) return;
    setAiConc({ loading: true, text: null, error: '' });
    try {
      const { text } = await aiConclusion(report.id, { with_photos: aiWithPhotos });
      setAiConc({ loading: false, text, error: '' });
    } catch (e) {
      setAiConc({ loading: false, text: null, error: t(aiErrorKey(e.response?.data?.detail)) });
    }
  };

  // AI: proofread the whole report and list the fields needing a correction
  const handleRunReview = async () => {
    if (!report) return;
    setAiReviewState({ loading: true, items: null, checked: 0, skipped: 0, error: '' });
    try {
      const { items, checked, skipped = 0 } = await aiReview(report.id);
      setAiReviewState({ loading: false, items, checked, skipped, error: '' });
    } catch (e) {
      setAiReviewState({
        loading: false, items: null, checked: 0,
        error: t(aiErrorKey(e.response?.data?.detail)),
      });
    }
  };

  const dropReviewItem = (target) =>
    setAiReviewState((prev) => ({
      ...prev,
      items: (prev.items || []).filter((it) => it.target !== target),
    }));

  // Route a correction back to the field it came from. Each write lands in its
  // own debounce slot, so applying several in a row cannot cancel the others.
  const applyReviewItem = (item) => {
    const { target, corrected } = item;
    if (target === 'description' || target === 'title') {
      handleFormChange(target, corrected);
    } else if (target === 'conclusion') {
      handleConclusionChange(corrected);
    } else if (target.startsWith('step:')) {
      const [, stepId, field] = target.split(':');
      handleStepContentChange(stepId, field, corrected);
    } else if (target.startsWith('checklist:')) {
      const itemId = target.slice('checklist:'.length);
      const ci = (report.checklist_items || []).find((c) => c.id === itemId);
      if (ci) handleChecklistTextChange(ci, corrected);
    } else if (target.startsWith('image:')) {
      // A photo caption: image:<step id>:<image id>.
      const [, stepId, imageId] = target.split(':');
      handleImageCommentChange(stepId, imageId, corrected);
    }
    dropReviewItem(target);
  };

  // Checklist
  const handleChecklistToggle = async (item) => {
    if (!report) return;
    const newChecked = !item.checked;
    setReport((prev) => ({
      ...prev,
      checklist_items: prev.checklist_items.map((ci) =>
        ci.id === item.id ? { ...ci, checked: newChecked } : ci
      ),
    }));
    try {
      await updateChecklistItem(report.id, item.id, { checked: newChecked });
      flashSaved();
    } catch {
      setReport((prev) => ({
        ...prev,
        checklist_items: prev.checklist_items.map((ci) =>
          ci.id === item.id ? { ...ci, checked: !newChecked } : ci
        ),
      }));
    }
  };

  const handleChecklistTextChange = (item, newText) => {
    setReport((prev) => ({
      ...prev,
      checklist_items: prev.checklist_items.map((ci) =>
        ci.id === item.id ? { ...ci, report_text: newText } : ci
      ),
    }));
    debounceSave(`checklist:${item.id}`, async () => {
      try {
        await updateChecklistItem(report.id, item.id, { report_text: newText });
        flashSaved();
      } catch {
        // silent
      }
    });
  };

  const patchChecklist = (fn) => setReport((prev) => ({ ...prev, checklist_items: fn(prev.checklist_items || []) }));

  const handleAddPoint = async (e) => {
    e.preventDefault();
    const label = newPoint.trim();
    if (!report || !label) return;
    setPointError('');
    try {
      const item = await addChecklistItem(report.id, label);
      patchChecklist((items) => [...items, item]);
      setNewPoint('');
      flashSaved();
    } catch (err) {
      setPointError(apiErrorMessage(err, t));
    }
  };

  const handleRenamePoint = async () => {
    if (!report || !renaming) return;
    const label = renaming.label.trim();
    const item = (report.checklist_items || []).find((ci) => ci.id === renaming.id);
    setRenaming(null);
    if (!item || !label || label === item.label) return;
    patchChecklist((items) => items.map((ci) => (ci.id === item.id ? { ...ci, label } : ci)));
    try {
      await updateChecklistItem(report.id, item.id, { label });
      flashSaved();
    } catch (err) {
      patchChecklist((items) => items.map((ci) => (ci.id === item.id ? { ...ci, label: item.label } : ci)));
      setPointError(apiErrorMessage(err, t));
    }
  };

  // A client created from the dialog is selected right away.
  const handleClientCreated = (entity, c) => {
    setCustomers((prev) => [...prev, c]);
    if (entity === 'company') handleFormChange('requesting_company_id', c.id);
    else handleFormChange('customer_id', c.id, c);
    setNewClientFor(null);
  };

  // Step management
  const handleStepContentChange = async (stepId, field, value) => {
    if (!report) return;
    setReport((prev) => ({
      ...prev,
      steps: prev.steps.map((s) => (s.id === stepId ? { ...s, [field]: value } : s)),
    }));
    debounceSave(`step:${stepId}:${field}`, async () => {
      try {
        await updateStep(report.id, stepId, { [field]: value });
        flashSaved();
      } catch {
        // silent
      }
    });
  };

  const handleAddStep = async (kind = 'text') => {
    if (!report) return;
    const order = report.steps.length;
    const title = kind === 'photos' ? t('wizard.photoStepTitle') : '';
    const step = await createStep(report.id, { title, content: '', order, kind });
    setReport((prev) => ({ ...prev, steps: [...prev.steps, step] }));
    setActiveStepIdx(order);
  };

  // Deleting a step takes its text and every photo with it: it is always
  // confirmed first (the button was once mistaken for "delete the photo").
  const handleDeleteStep = (step) => setPendingDelete({ kind: 'step', step });

  const deleteStepNow = async (stepId) => {
    await deleteStep(report.id, stepId);
    setReport((prev) => ({
      ...prev,
      steps: prev.steps.filter((s) => s.id !== stepId),
    }));
    setActiveStepIdx((prev) => Math.max(0, prev - 1));
  };

  // Image management
  const patchStepImages = useCallback((stepId, fn) => {
    setReport((prev) => (prev ? {
      ...prev,
      steps: prev.steps.map((s) => (s.id === stepId ? { ...s, images: fn(s.images || []) } : s)),
    } : prev));
  }, []);

  // Every photo shows up at once, from the device, while it uploads — before,
  // nothing appeared until the (unshrunk, several-MB) upload had finished, and
  // a failed one vanished without a word.
  const handleUploadImages = async (stepId, files) => {
    if (!report) return;
    const batch = Array.from(files).map((file, i) => ({
      file,
      placeholder: {
        id: `uploading-${Date.now()}-${i}`,
        filename: '',
        original_filename: file.name,
        annotated_filename: '',
        comment: '',
        _uploading: true,
        _localUrl: URL.createObjectURL(file),
      },
    }));
    patchStepImages(stepId, (imgs) => [...imgs, ...batch.map((b) => b.placeholder)]);

    for (const { file, placeholder } of batch) {
      try {
        const shrunk = await compressImage(file);
        const img = await uploadImage(report.id, stepId, shrunk, '', 0);
        // Keep showing the local copy: no blank frame while the server one loads.
        const shown = img._localUrl ? img : { ...img, _localUrl: placeholder._localUrl };
        patchStepImages(stepId, (imgs) => imgs.map((it) => (it.id === placeholder.id ? shown : it)));
      } catch (e) {
        const detail = e.response?.data?.detail;
        patchStepImages(stepId, (imgs) => imgs.map((it) => (it.id === placeholder.id
          ? { ...it, _uploading: false, _uploadError: typeof detail === 'string' ? detail : t('upload.failed') }
          : it)));
      }
    }
  };

  const dismissFailedUpload = (stepId, imageId) =>
    patchStepImages(stepId, (imgs) => imgs.filter((it) => it.id !== imageId));

  // Photos queued offline (or on a connection too weak to finish) are sent
  // later by the offline queue. When it drains, swap their placeholders for
  // the real photos — otherwise they stayed "pending" until a full reload.
  const reportRef = useRef(report);
  reportRef.current = report;
  useEffect(() => subscribeOfflineQueue(async (count) => {
    const current = reportRef.current;
    if (!current || count > 0) return;
    const waiting = (current.steps || []).filter((s) => (s.images || []).some((i) => i._pendingUpload));
    for (const step of waiting) {
      try {
        const fresh = await listStepImages(current.id, step.id);
        patchStepImages(step.id, (imgs) => {
          const local = new Map(imgs.filter((i) => i._localUrl).map((i) => [i.id, i._localUrl]));
          return fresh.map((i) => (local.has(i.id) ? { ...i, _localUrl: local.get(i.id) } : i));
        });
      } catch {
        // still unreachable: the next drain will try again
      }
    }
  }), [patchStepImages]);

  /** Photos the phone just sent, folded into the step already on screen. */
  const handlePhoneImages = useCallback((stepId, images) => {
    setReport((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        steps: prev.steps.map((s) => {
          if (s.id !== stepId) return s;
          const known = new Set((s.images || []).map((img) => img.id));
          const fresh = images.filter((img) => !known.has(img.id));
          return fresh.length ? { ...s, images: [...s.images, ...fresh] } : s;
        }),
      };
    });
  }, []);

  // The whole step panel is the drop target, not just the strip — nobody should
  // have to aim at a small band. The active step is read at drop time, so it is
  // always the step on screen that receives the photos.
  const [draggingPhotos, photoDropHandlers] = useFileDrop((files) => {
    const step = (report?.steps || [])[activeStepIdx];
    if (step) handleUploadImages(step.id, files);
  });

  const handleImageCommentChange = async (stepId, imageId, comment) => {
    if (!report) return;
    setReport((prev) => ({
      ...prev,
      steps: prev.steps.map((s) =>
        s.id === stepId
          ? { ...s, images: s.images.map((img) => (img.id === imageId ? { ...img, comment } : img)) }
          : s
      ),
    }));
    if (String(imageId).startsWith('uploading-')) return; // not on the server yet
    debounceSave(`image:${imageId}:comment`, async () => {
      try {
        await updateImage(report.id, stepId, imageId, { comment });
        flashSaved();
      } catch {
        // silent
      }
    });
  };

  const handleDeleteImage = (stepId, image, n) =>
    setPendingDelete({ kind: 'image', stepId, image, n });

  const deleteImageNow = async (stepId, imageId) => {
    await deleteImage(report.id, stepId, imageId);
    setReport((prev) => ({
      ...prev,
      steps: prev.steps.map((s) =>
        s.id === stepId ? { ...s, images: s.images.filter((img) => img.id !== imageId) } : s
      ),
    }));
  };

  const confirmPendingDelete = async () => {
    if (!report || !pendingDelete) return;
    setDeleting(true);
    try {
      if (pendingDelete.kind === 'step') await deleteStepNow(pendingDelete.step.id);
      else if (pendingDelete.kind === 'checkpoint') {
        await deleteChecklistItem(report.id, pendingDelete.item.id);
        patchChecklist((items) => items.filter((ci) => ci.id !== pendingDelete.item.id));
      } else await deleteImageNow(pendingDelete.stepId, pendingDelete.image.id);
      setPendingDelete(null);
    } catch (err) {
      setPendingDelete(null);
      alert(apiErrorMessage(err, t));
    } finally {
      setDeleting(false);
    }
  };

  // Moving a photo changes its place and so its number ("Photo n"), which
  // follows the order everywhere: here, in the PDF and in the client link.
  const moveImage = async (stepId, fromIdx, toIdx) => {
    const step = (report?.steps || []).find((s) => s.id === stepId);
    const images = step?.images || [];
    if (!step || fromIdx === toIdx || toIdx < 0 || toIdx >= images.length) return;
    const reordered = [...images];
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);
    setOrderError('');
    patchStepImages(stepId, () => reordered);
    try {
      await reorderImages(report.id, stepId, reordered.map((img) => img.id));
      flashSaved();
    } catch (err) {
      // 409: photos arrived meanwhile (phone, other device) — show the
      // server's order rather than guess.
      try {
        const fresh = await listStepImages(report.id, stepId);
        patchStepImages(stepId, (imgs) => {
          const local = new Map(imgs.filter((i) => i._localUrl).map((i) => [i.id, i._localUrl]));
          return fresh.map((i) => (local.has(i.id) ? { ...i, _localUrl: local.get(i.id) } : i));
        });
      } catch {
        patchStepImages(stepId, () => images);
      }
      setOrderError(err?.response?.status === 409 ? t('wizard.orderChanged') : apiErrorMessage(err, t));
    }
  };

  // Only the photo and its order bar start a drag: a draggable card would get
  // in the way of selecting text in the caption.
  const dragHandle = (image, canMove) => (canMove ? {
    draggable: true,
    onDragStart: (e) => {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/x-expertisr-photo', image.id);
      setDragImageId(image.id);
    },
    onDragEnd: () => setDragImageId(null),
  } : {});

  const handleAnnotationSave = async (stepId, imageId, blob, stateJson) => {
    if (!report) return;
    const annotations = JSON.stringify(stateJson);
    const file = new File([blob], 'annotated.jpg', { type: blob.type || 'image/jpeg' });
    const updated = await uploadAnnotatedImage(report.id, stepId, imageId, file);
    await updateImage(report.id, stepId, imageId, { annotations_json: annotations });
    // The render we just uploaded is shown as is: no round-trip to fetch it back.
    const localUrl = URL.createObjectURL(blob);
    patchStepImages(stepId, (imgs) => imgs.map((img) => (img.id === imageId
      ? { ...img, annotated_filename: updated.annotated_filename, annotations_json: annotations, _localUrl: localUrl }
      : img)));
    flashSaved();
    setAnnotatingImage(null);
  };

  // PDF
  const handleGeneratePdf = async () => {
    if (!report) return;
    setPdfLoading(true);
    try {
      // Opens in a new tab (the report is saved first, so the PDF is current).
      await openPdfInNewTab(async () => {
        await updateReport(report.id, { conclusion, status: 'completed' });
        return downloadPdf(report.id);
      }, `rapport_${report.reference || report.id}.pdf`);
    } catch {
      alert(t('errors.pdfFailed'));
    } finally {
      setPdfLoading(false);
    }
  };

  // Top-bar PDF: from any page, so it must not complete the report — only the
  // last page's "Generate" does that. Saves the content, leaves the status alone.
  const handleTopBarPdf = async () => {
    if (!report) return;
    setPdfLoading(true);
    try {
      await openPdfInNewTab(async () => {
        await flushPendingSaves();
        await updateReport(report.id, { conclusion });
        return downloadPdf(report.id);
      }, `rapport_${report.reference || report.id}.pdf`);
    } catch {
      alert(t('errors.pdfFailed'));
    } finally {
      setPdfLoading(false);
    }
  };

  const handleTopBarSend = async () => {
    setSentNotice('');
    await flushPendingSaves();
    setSendOpen(true);
  };

  // Navigation: the page reached is saved on the report, so it reopens there.
  const goToPage = (idx) => {
    if (idx < 0 || idx >= WIZARD_PAGES.length || idx === wizardPage) return;
    setWizardPage(idx);
    if (report) updateReport(report.id, { current_step: idx }).catch(() => {});
  };
  const goNext = () => goToPage(wizardPage + 1);
  const goPrev = () => goToPage(wizardPage - 1);

  // The step open on "Steps" is remembered on this device, by id (an index
  // would point elsewhere once a step is deleted).
  const activeStepId = report?.steps?.[activeStepIdx]?.id;
  useEffect(() => {
    if (report?.id && activeStepId) writeLastStep(report.id, activeStepId);
  }, [report?.id, activeStepId]);

  // --- Rendering ---

  // "Photo n°" as the PDF prints it (see utils/photoNumbers).
  const numbers = useMemo(() => photoNumbers(report?.steps), [report?.steps]);

  if (loading) return <p style={{ textAlign: 'center', paddingTop: 40 }}>{t('auth.loading')}</p>;

  // Crop: a separate render; annotations drawn before are voided by the server.
  const handleCropApply = async (stepId, imageId, blob, frame) => {
    const file = new File([blob], 'cropped.jpg', { type: 'image/jpeg' });
    const updated = await uploadCroppedImage(report.id, stepId, imageId, file, frame);
    const localUrl = URL.createObjectURL(blob);
    patchStepImages(stepId, (imgs) => imgs.map((img) => (img.id === imageId
      ? {
        ...img,
        cropped_filename: updated.cropped_filename,
        crop_json: updated.crop_json,
        annotated_filename: '',
        annotations_json: '',
        _localUrl: localUrl,
      }
      : img)));
    setCroppingImage(null);
    flashSaved();
  };

  const handleCropReset = async (stepId, imageId) => {
    await resetCroppedImage(report.id, stepId, imageId);
    patchStepImages(stepId, (imgs) => imgs.map((img) => (img.id === imageId
      ? {
        ...img, cropped_filename: '', crop_json: '', annotated_filename: '', annotations_json: '', _localUrl: undefined,
      }
      : img)));
    setCroppingImage(null);
    flashSaved();
  };

  let cropper = null;
  if (croppingImage) {
    const { stepId, image } = croppingImage;
    const n = numbers.get(image.id);
    cropper = (
      <ImageCropper
        reportId={report.id}
        filename={image.filename}
        savedCrop={image.crop_json}
        hasCrop={Boolean(image.cropped_filename)}
        hasAnnotations={Boolean(image.annotated_filename)}
        title={n ? t('crop.titleN', { n }) : t('crop.title')}
        onApply={(blob, frame) => handleCropApply(stepId, image.id, blob, frame)}
        onReset={() => handleCropReset(stepId, image.id)}
        onCancel={() => setCroppingImage(null)}
      />
    );
  }

  // Annotation editor: laid over the wizard, which stays mounted underneath —
  // closing it leaves the expert on the same photo, at the same scroll position.
  let annotator = null;
  if (annotatingImage) {
    const { stepId, image } = annotatingImage;
    let existingState = null;
    if (image.annotations_json) {
      try {
        existingState = JSON.parse(image.annotations_json);
      } catch {
        // ignore
      }
    }
    const n = numbers.get(image.id);
    annotator = (
      <ImageAnnotator
        reportId={report.id}
        filename={image.cropped_filename || image.filename}
        existingState={existingState}
        title={n ? t('wizard.annotatePhotoN', { n }) : t('wizard.annotate')}
        onSave={(blob, state) => handleAnnotationSave(stepId, image.id, blob, state)}
        onCancel={() => setAnnotatingImage(null)}
      />
    );
  }

  // --- Entity selector helper ---
  // An empty billing client means the on-site client pays: say so by name.
  const chosenCustomer = customers.find((c) => c.id === form.customer_id);
  const billingPlaceholder = chosenCustomer
    ? t('wizard.sameAsCustomer', { name: chosenCustomer.name, interpolation: { escapeValue: false } })
    : t('wizard.selectCompany');

  const renderEntitySelector = (fieldName, entities, label, selectLabel, entity) => (
    <div className="form-group">
      <label htmlFor={`report-${fieldName}`}>{label}</label>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <select
          id={`report-${fieldName}`}
          className="form-control"
          value={form[fieldName]}
          // handleFormChange only saves once the report exists, so it serves
          // the new-report form too — and applies the address default there.
          onChange={(e) => handleFormChange(fieldName, e.target.value)}
        >
          <option value="">{selectLabel}</option>
          {entities.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        <button
          className="btn btn-secondary btn-sm"
          type="button"
          onClick={() => setNewClientFor(entity)}
          aria-label={t('clients.newTitle', { context: entity })}
          title={t('clients.newTitle', { context: entity })}
          style={{ flexShrink: 0 }}
        >
          +
        </button>
      </div>
    </div>
  );

  const newClientDialog = newClientFor && (
    <NewClientDialog
      entity={newClientFor}
      onCreated={(c) => handleClientCreated(newClientFor, c)}
      onClose={() => setNewClientFor(null)}
    />
  );

  // New report form (no id yet)
  if (!id && !report) {
    return (
      <div className="card fade-in" style={{ maxWidth: 600, margin: '40px auto' }}>
        {newClientDialog}
        <h2 style={{ marginBottom: 24, fontSize: 22, fontWeight: 700 }}>{t('wizard.reportInfo')}</h2>
        <div className="form-group">
          <label>{t('wizard.title')} *</label>
          <input
            className="form-control"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder={t('wizard.title')}
          />
        </div>
        <div className="form-group">
          <label>{t('wizard.reference')}</label>
          <input
            className="form-control"
            value={form.reference}
            onChange={(e) => setForm({ ...form, reference: e.target.value })}
          />
        </div>

        {renderEntitySelector(
          'requesting_company_id', customers, t('wizard.requestingCompany'), billingPlaceholder,
          'company',
        )}
        {renderEntitySelector(
          'customer_id', customers, t('wizard.customer'), t('wizard.selectCustomer'),
          'customer',
        )}

        <div className="form-group">
          <label>{t('wizard.clientName')}</label>
          <input
            className="form-control"
            value={form.client_name}
            onChange={(e) => setForm({ ...form, client_name: e.target.value })}
          />
        </div>
        <button className="btn btn-primary" onClick={handleCreate} disabled={saving || !form.title.trim()}>
          {t('reports.create')}
        </button>
      </div>
    );
  }

  if (!report) return <p style={{ textAlign: 'center', paddingTop: 40 }}>{t('auth.loading')}</p>;

  const steps = report.steps || [];
  const activeStep = steps[activeStepIdx] || null;
  const totalWizardSteps = WIZARD_PAGES.length;
  const hasConclusion = conclusion.trim().length > 0;
  const wizardLabels = [
    t('wizard.infoStep'),
    t('wizard.stepsStep'),
    t('wizard.checklistStep'),
    t('wizard.conclusionStep'),
  ];

  return (
    <div className="fade-in">
      {annotator}
      {cropper}
      {newClientDialog}
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h2 style={{ fontSize: 22, fontWeight: 700 }}>{report.title}</h2>
        <span className={`saved-indicator ${savedVisible ? 'visible' : ''}`}>
          &#10003; {t('wizard.saved')}
        </span>
      </div>

      {/* Top bar: PDF and e-mail from any page. Both present the conclusion, so
          they wait for it — the title says why. */}
      <div className="wizard-actionbar">
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={handleTopBarPdf}
          disabled={pdfLoading || !hasConclusion}
          title={hasConclusion ? t('reports.viewPdf') : t('sendReport.needConclusion')}
        >
          {pdfLoading ? t('wizard.generating') : t('reports.pdf')}
        </button>
        <button
          type="button"
          className="btn btn-secondary btn-sm send-btn"
          onClick={handleTopBarSend}
          disabled={!hasConclusion}
          title={hasConclusion ? t('sendReport.open') : t('sendReport.needConclusion')}
        >
          <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" width="14" height="14" style={{ flex: 'none' }}>
            <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
            <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
          </svg>
          {t('sendReport.button')}
        </button>
      </div>
      {sentNotice && <p className="send-notice" role="status">{sentNotice}</p>}
      {sendOpen && (
        <SendReportDialog
          reportId={report.id}
          onSent={(sent) => {
            setSendOpen(false);
            // The server completes a report once it is sent.
            setReport((prev) => ({ ...prev, status: 'completed' }));
            setSentNotice(t('sendReport.sent', { list: sent.recipients.join(', '), interpolation: { escapeValue: false } }));
          }}
          onClose={() => setSendOpen(false)}
        />
      )}

      {/* Wizard progress */}
      <div className="wizard-progress">
        {WIZARD_PAGES.map((page, idx) => (
          <React.Fragment key={page}>
            <div
              className={`step-indicator ${idx === wizardPage ? 'active' : ''} ${idx < wizardPage ? 'done' : ''}`}
              onClick={() => goToPage(idx)}
              title={wizardLabels[idx]}
            >
              {idx < wizardPage ? '\u2713' : idx + 1}
            </div>
            {idx < totalWizardSteps - 1 && (
              <div className={`step-connector ${idx < wizardPage ? 'done' : ''}`} />
            )}
          </React.Fragment>
        ))}
      </div>
      <div className="wizard-labels">
        {wizardLabels.map((label, idx) => (
          <span key={idx} className={idx === wizardPage ? 'active' : ''}>{label}</span>
        ))}
      </div>

      {/* Page 0: Report info */}
      {wizardPage === 0 && (
        <div className="card">
          <h3 style={{ marginBottom: 20, fontSize: 18 }}>{t('wizard.reportInfo')}</h3>
          {[
            ['title', t('wizard.title')],
            ['reference', t('wizard.reference')],
            ['description', t('wizard.description')],
            ['location', t('wizard.location')],
            ['expertise_date', t('wizard.expertiseDate')],
          ].map(([field, label]) => (
            <div className="form-group" key={field}>
              <label>{label}</label>
              {field === 'description' ? (
                <>
                  <textarea
                    className="form-control"
                    value={form[field]}
                    onChange={(e) => handleFormChange(field, e.target.value)}
                  />
                  {aiStatus.enabled && report && (
                    <AiEnrich
                      reportId={report.id}
                      kind="description"
                      value={form.description}
                      onApply={(text) => handleFormChange('description', text)}
                    />
                  )}
                </>
              ) : (
                <input
                  className="form-control"
                  type={field === 'expertise_date' ? 'date' : 'text'}
                  value={form[field]}
                  onChange={(e) => handleFormChange(field, e.target.value)}
                />
              )}
            </div>
          ))}

          {renderEntitySelector(
            'requesting_company_id', customers, t('wizard.requestingCompany'), billingPlaceholder,
            'company',
          )}
          {renderEntitySelector(
            'customer_id', customers, t('wizard.customer'), t('wizard.selectCustomer'),
            'customer',
          )}

          <div className="form-group">
            <label>{t('wizard.clientName')}</label>
            <input
              className="form-control"
              value={form.client_name}
              onChange={(e) => handleFormChange('client_name', e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>{t('wizard.clientAddress')}</label>
            <textarea
              className="form-control"
              value={form.client_address}
              onChange={(e) => handleFormChange('client_address', e.target.value)}
            />
          </div>
        </div>
      )}

      {/* Page 1: Steps with images */}
      {wizardPage === 1 && (
        <div>
          <div className="step-pills">
            {steps.map((step, idx) => (
              <button
                key={step.id}
                className={`step-pill ${idx === activeStepIdx ? 'active' : ''}`}
                onClick={() => setActiveStepIdx(idx)}
              >
                {step.title || `${t('wizard.step')} ${idx + 1}`}
              </button>
            ))}
            <button className="step-pill step-pill-add" onClick={() => handleAddStep('text')}>
              + {t('wizard.addStep')}
            </button>
            <button className="step-pill step-pill-add" onClick={() => handleAddStep('photos')}>
              + {t('wizard.addPhotoStep')}
            </button>
          </div>

          {activeStep && (
            <div className="card step-card" {...photoDropHandlers}>
              {draggingPhotos && (
                <div className="step-dragover">{t('upload.dropOnStep')}</div>
              )}
              <div className="form-group">
                <div className="step-title-row">
                  <label htmlFor="step-title">{t('wizard.stepTitle')}</label>
                  {steps.length > 1 && (
                    <button
                      type="button"
                      className="btn-step-delete"
                      onClick={() => handleDeleteStep(activeStep)}
                    >
                      {t('wizard.removeStep')}
                    </button>
                  )}
                </div>
                <input
                  id="step-title"
                  className="form-control"
                  value={activeStep.title}
                  onChange={(e) => handleStepContentChange(activeStep.id, 'title', e.target.value)}
                />
              </div>
              {activeStep.kind === 'photos' ? (
                <p className="step-kind-hint">{t('wizard.photoStepHint')}</p>
              ) : (
              <div className="form-group">
                <label>{t('wizard.stepContent')}</label>
                <textarea
                  className="form-control"
                  style={{ minHeight: 150 }}
                  value={activeStep.content}
                  onChange={(e) => handleStepContentChange(activeStep.id, 'content', e.target.value)}
                />
                {aiStatus.enabled && (
                  // Keyed on the step: without it React reuses the instance
                  // across steps and a suggestion generated for step A stays
                  // on screen — and gets applied — on step B.
                  <AiEnrich
                    key={activeStep.id}
                    reportId={report.id}
                    kind="step"
                    value={activeStep.content}
                    onApply={(text) => handleStepContentChange(activeStep.id, 'content', text)}
                  />
                )}
              </div>
              )}

              <PhotoDropzone
                label={t('wizard.uploadImages')}
                onFiles={(files) => handleUploadImages(activeStep.id, files)}
                dragging={draggingPhotos}
                dropHandlers={photoDropHandlers}
              >
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setQrStepId(activeStep.id)}
                >
                  {t('upload.fromPhone')}
                </button>
              </PhotoDropzone>

              {orderError && <div className="ai-error" role="alert">{orderError}</div>}

              {activeStep.images && activeStep.images.length > 0 && (
                <div className={`images-grid ${activeStep.kind === 'photos' ? 'images-grid-wide' : ''}`}>
                  {activeStep.images.map((image, idx) => {
                    const n = numbers.get(image.id);
                    const busy = image._pendingUpload || image._uploading || image._uploadError;
                    // The order can only be saved once every photo of the step
                    // is on the server.
                    const canMove = activeStep.images.length > 1
                      && !activeStep.images.some((i) => i._pendingUpload || i._uploading || i._uploadError);
                    return (
                    <div
                      className={`image-card ${busy ? 'image-pending' : ''} ${image._uploadError ? 'image-failed' : ''} ${dragImageId === image.id ? 'image-dragging' : ''}`}
                      key={image.id}
                      data-testid="image-card"
                      onDragOver={(e) => {
                        if (dragImageId && dragImageId !== image.id) e.preventDefault();
                      }}
                      onDrop={(e) => {
                        if (!dragImageId) return;
                        e.preventDefault();
                        e.stopPropagation();
                        const from = activeStep.images.findIndex((i) => i.id === dragImageId);
                        setDragImageId(null);
                        if (from >= 0) moveImage(activeStep.id, from, idx);
                      }}
                    >
                      {canMove && (
                        <div className="image-order-bar" {...dragHandle(image, canMove)}>
                          <button
                            type="button"
                            className="image-order-btn"
                            disabled={idx === 0}
                            aria-label={t('wizard.movePhotoBefore', { n })}
                            title={t('wizard.movePhotoBefore', { n })}
                            onClick={() => moveImage(activeStep.id, idx, idx - 1)}
                          >
                            <span aria-hidden="true">←</span>
                          </button>
                          <span className="image-order-hint" aria-hidden="true">{t('wizard.dragToReorder')}</span>
                          <button
                            type="button"
                            className="image-order-btn"
                            disabled={idx === activeStep.images.length - 1}
                            aria-label={t('wizard.movePhotoAfter', { n })}
                            title={t('wizard.movePhotoAfter', { n })}
                            onClick={() => moveImage(activeStep.id, idx, idx + 1)}
                          >
                            <span aria-hidden="true">→</span>
                          </button>
                        </div>
                      )}
                      <div className="image-frame" {...dragHandle(image, canMove)}>
                        <AuthImage
                          reportId={report.id}
                          filename={image.annotated_filename || image.cropped_filename || image.filename}
                          localUrl={image._localUrl}
                          alt={image.comment || image.original_filename}
                          onClick={() => {
                            // Not on the server yet: nothing to annotate.
                            if (!busy) setAnnotatingImage({ stepId: activeStep.id, image });
                          }}
                        />
                        {n && <span className="photo-number" data-testid="photo-number">{t('wizard.photoN', { n })}</span>}
                      </div>
                      {image._uploading && (
                        <div className="image-pending-label">⏳ {t('upload.sending')}</div>
                      )}
                      {image._pendingUpload && (
                        <div className="image-pending-label">⏳ {t('offline.pendingPhoto')}</div>
                      )}
                      {image._uploadError ? (
                        <div className="image-actions">
                          <div className="ai-error" role="alert">{image._uploadError}</div>
                          <button className="btn btn-ghost btn-sm" onClick={() => dismissFailedUpload(activeStep.id, image.id)}>
                            {t('upload.dismiss')}
                          </button>
                        </div>
                      ) : (
                      <div className="image-actions">
                        <AutoGrowTextarea
                          placeholder={t('wizard.imageComment')}
                          value={image.comment || ''}
                          disabled={image._uploading}
                          onChange={(e) => handleImageCommentChange(activeStep.id, image.id, e.target.value)}
                        />
                        <div className="image-buttons">
                          <button className="btn btn-accent btn-sm"
                            disabled={busy}
                            title={busy ? t('offline.pendingPhoto') : undefined}
                            onClick={() => setAnnotatingImage({ stepId: activeStep.id, image })}>
                            {t('wizard.annotate')}
                          </button>
                          <button className="btn btn-secondary btn-sm"
                            disabled={busy}
                            title={busy ? t('offline.pendingPhoto') : undefined}
                            onClick={() => setCroppingImage({ stepId: activeStep.id, image })}>
                            {t('wizard.crop')}
                          </button>
                          <button className="btn btn-danger btn-sm"
                            disabled={image._uploading}
                            onClick={() => handleDeleteImage(activeStep.id, image, n)}>
                            {t('wizard.removeImage')}
                          </button>
                        </div>
                      </div>
                      )}
                    </div>
                    );
                  })}
                </div>
              )}

            </div>
          )}
        </div>
      )}

      {/* Page 2: Checklist */}
      {wizardPage === 2 && (
        <div className="card">
          <h3 style={{ marginBottom: 8, fontSize: 18 }}>{t('wizard.checklist')}</h3>
          <p style={{ color: 'var(--color-text-light)', marginBottom: 20, fontSize: 14 }}>
            {t('wizard.checklistDescription')}
          </p>
          {report.checklist_items && report.checklist_items.length > 0 ? (
            <div>
              {report.checklist_items.map((item) => (
                <div key={item.id} className="checklist-item">
                  <label className="checklist-label">
                    <input
                      type="checkbox"
                      checked={item.checked}
                      onChange={() => handleChecklistToggle(item)}
                      className="checklist-checkbox"
                    />
                    {renaming?.id === item.id ? null : (
                      <span style={{ color: item.checked ? 'var(--color-primary)' : 'var(--color-text)', fontWeight: item.checked ? 600 : 400 }}>
                        {item.label}
                      </span>
                    )}
                  </label>
                  {item.is_custom && renaming?.id === item.id && (
                    <div className="checkpoint-rename">
                      <input
                        className="form-control"
                        aria-label={t('wizard.pointRenameLabel')}
                        value={renaming.label}
                        maxLength={500}
                        autoFocus
                        onChange={(e) => setRenaming({ id: item.id, label: e.target.value })}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') { e.preventDefault(); handleRenamePoint(); }
                          if (e.key === 'Escape') { e.preventDefault(); setRenaming(null); }
                        }}
                      />
                      <button type="button" className="btn btn-primary btn-sm" onClick={handleRenamePoint}>
                        {t('clients.save')}
                      </button>
                      <button type="button" className="btn btn-secondary btn-sm" onClick={() => setRenaming(null)}>
                        {t('clients.cancel')}
                      </button>
                    </div>
                  )}
                  {item.is_custom && renaming?.id !== item.id && (
                    <div className="checkpoint-custom">
                      <span className="checkpoint-badge">{t('wizard.pointCustomBadge')}</span>
                      <button type="button" className="checkpoint-action" onClick={() => setRenaming({ id: item.id, label: item.label })}>
                        {t('wizard.pointRename')}
                      </button>
                      <button type="button" className="checkpoint-action is-danger" onClick={() => setPendingDelete({ kind: 'checkpoint', item })}>
                        {t('wizard.pointDelete')}
                      </button>
                    </div>
                  )}
                  {item.checked && (
                    <div className="checklist-text">
                      <textarea
                        className="form-control"
                        value={item.report_text || ''}
                        onChange={(e) => handleChecklistTextChange(item, e.target.value)}
                        placeholder={t('wizard.checklistReportTextPlaceholder')}
                        rows={3}
                      />
                      {aiStatus.enabled && (
                        <AiEnrich
                          reportId={report.id}
                          kind="checklist"
                          value={item.report_text}
                          onApply={(text) => handleChecklistTextChange(item, text)}
                        />
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: 'var(--color-text-light)', fontStyle: 'italic' }}>
              {t('wizard.checklistDescription')}
            </p>
          )}

          <form className="checkpoint-add" onSubmit={handleAddPoint}>
            <label htmlFor="checkpoint-new">{t('wizard.pointAddLabel')}</label>
            <div className="checkpoint-add-row">
              <input
                id="checkpoint-new"
                className="form-control"
                value={newPoint}
                maxLength={500}
                placeholder={t('wizard.pointAddPlaceholder')}
                onChange={(e) => setNewPoint(e.target.value)}
              />
              <button type="submit" className="btn btn-primary btn-sm" disabled={!newPoint.trim()}>
                {t('wizard.pointAdd')}
              </button>
            </div>
            <p className="checkpoint-hint">{t('wizard.pointAddHint')}</p>
            {pointError && <div className="ai-error" role="alert">{pointError}</div>}
          </form>
        </div>
      )}

      {/* Page 3: Conclusion + PDF */}
      {wizardPage === 3 && (
        <div className="card">
          <h3 style={{ marginBottom: 20, fontSize: 18 }}>{t('wizard.conclusion')}</h3>

          {aiStatus.enabled && (
            <div className="ai-conclusion-bar">
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={handleGenerateConclusion}
                disabled={aiConc.loading}
              >
                ✨ {aiConc.loading ? t('ai.working') : t('ai.writeConclusion')}
              </button>
              {aiStatus.photos_allowed && (
                <label className="ai-photos-toggle" title={t('ai.withPhotosHint')}>
                  <input
                    type="checkbox"
                    checked={aiWithPhotos}
                    onChange={(e) => setAiWithPhotos(e.target.checked)}
                  />
                  {t('ai.withPhotos')}
                </label>
              )}
              {aiConc.error && <span className="ai-error">{aiConc.error}</span>}
            </div>
          )}
          {aiStatus.enabled && (
            <div className="ai-review">
              <div className="ai-conclusion-bar">
                <span className="ai-conclusion-label">✓ {t('ai.reviewTitle')}</span>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={handleRunReview}
                  disabled={aiReviewState.loading}
                  title={t('ai.reviewHint')}
                >
                  {aiReviewState.loading ? t('ai.working') : t('ai.reviewRun')}
                </button>
                {aiReviewState.error && <span className="ai-error">{aiReviewState.error}</span>}
              </div>

              {aiReviewState.skipped > 0 && (
                <p className="ai-error" role="status">
                  {t('ai.reviewSkipped', { count: aiReviewState.skipped })}
                </p>
              )}

              {aiReviewState.items && aiReviewState.items.length === 0 && (
                <p className="ai-ok">
                  {t('ai.reviewClean', { n: aiReviewState.checked })}
                </p>
              )}

              {aiReviewState.items && aiReviewState.items.length > 0 && (
                <div className="ai-review-list">
                  <div className="ai-review-head">
                    <span>
                      {t('ai.reviewFound', {
                        n: aiReviewState.items.length,
                        checked: aiReviewState.checked,
                      })}
                    </span>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={() => [...aiReviewState.items].forEach(applyReviewItem)}
                    >
                      {t('ai.reviewApplyAll')}
                    </button>
                  </div>
                  {aiReviewState.items.map((item) => (
                    <div className="ai-review-item" key={item.target}>
                      <div className="ai-review-label">{item.label}</div>
                      {item.error ? (
                        <div className="ai-error">{t(aiErrorKey(item.error))}</div>
                      ) : (
                        <>
                          <DiffText before={item.original} after={item.corrected} />
                          <div className="ai-preview-actions">
                            <button
                              type="button"
                              className="btn btn-primary btn-sm"
                              onClick={() => applyReviewItem(item)}
                            >
                              {t('ai.replace')}
                            </button>
                            <button
                              type="button"
                              className="btn btn-ghost btn-sm"
                              onClick={() => dropReviewItem(item.target)}
                            >
                              {t('ai.ignore')}
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {aiConc.text != null && (
            <AiPreview
              text={aiConc.text}
              applyLabel={t('ai.useThisConclusion')}
              onApply={() => {
                handleConclusionChange(aiConc.text);
                setAiConc({ loading: false, text: null, error: '' });
              }}
              onDiscard={() => setAiConc({ loading: false, text: null, error: '' })}
            />
          )}

          <div className="form-group">
            <textarea
              className="form-control"
              style={{ minHeight: 200 }}
              placeholder={t('wizard.conclusionPlaceholder')}
              value={conclusion}
              onChange={(e) => handleConclusionChange(e.target.value)}
            />
            {aiStatus.enabled && (
              <AiEnrich
                reportId={report.id}
                kind="conclusion"
                value={conclusion}
                onApply={(text) => handleConclusionChange(text)}
              />
            )}
          </div>
          <button
            className="btn btn-accent"
            style={{ marginTop: 8 }}
            onClick={handleGeneratePdf}
            disabled={pdfLoading}
          >
            {pdfLoading ? t('wizard.generating') : t('wizard.generatePdf')}
          </button>
          <ShareLinkPanel reportId={report.id} />
        </div>
      )}

      {/* Navigation buttons */}
      <div className="wizard-actions">
        <button className="btn btn-secondary" onClick={goPrev} disabled={wizardPage === 0}>
          {t('wizard.previous')}
        </button>
        <button className="btn btn-primary" onClick={goNext} disabled={wizardPage >= totalWizardSteps - 1}>
          {t('wizard.next')}
        </button>
      </div>

      {pendingDelete && (
        <ConfirmDialog
          busy={deleting}
          onCancel={() => setPendingDelete(null)}
          onConfirm={confirmPendingDelete}
          {...(pendingDelete.kind === 'checkpoint' ? {
            title: t('wizard.confirmDeletePointTitle', {
              label: pendingDelete.item.label,
              interpolation: { escapeValue: false },
            }),
            confirmLabel: t('wizard.pointDelete'),
          } : pendingDelete.kind === 'step' ? {
            title: t('wizard.confirmDeleteStepTitle', {
              title: pendingDelete.step.title || t('wizard.step'),
              // React escapes it; i18next escaping would show "Salle d&#39;eau".
              interpolation: { escapeValue: false },
            }),
            confirmLabel: t('wizard.confirmDeleteStep'),
          } : {
            title: pendingDelete.n
              ? t('wizard.confirmDeleteImageTitle', { n: pendingDelete.n })
              : t('wizard.confirmDeleteThisImage'),
            confirmLabel: t('wizard.confirmDeleteImage'),
          })}
        >
          {pendingDelete.kind === 'checkpoint' ? (
            <p>{t('wizard.confirmDeletePointBody')}</p>
          ) : pendingDelete.kind === 'step' ? (
            <p>
              {t('wizard.confirmDeleteStepBody', {
                count: (pendingDelete.step.images || []).length,
              })}
            </p>
          ) : (
            <p>{t('wizard.confirmDeleteImageBody')}</p>
          )}
        </ConfirmDialog>
      )}

      {qrStepId && (
        <MobileUploadModal
          reportId={report.id}
          stepId={qrStepId}
          knownImageIds={
            (steps.find((s) => s.id === qrStepId)?.images || []).map((img) => img.id)
          }
          onClose={() => setQrStepId(null)}
          onReceived={(images) => handlePhoneImages(qrStepId, images)}
        />
      )}
    </div>
  );
}
